import {capitalCase} from 'change-case';
import {
    AppointmentStatus,
    OrderAppointmentStatus,
    ColorClass,
    IncidentStatus,
    MaterialStatus,
    PurchaseOrderStatus,
    EmptyCheckInStatus,
    ShuntPriority,
    StorageUnitStatus,
    EmptyCheckOutStatus,
    PositionStatus,
    PreProductionStatus,
    ProductionStatus,
    InvoicePurchaseOrderStatus,
    InvoiceStatus,
    CreditRequestStatus,
    NestleStatus,
    UserStatus,
} from "@services";

class EnumUtils {
    public static readonly BootstrapColors = [
        {name: ColorClass.Primary, var: '--bs-primary'},
        {name: ColorClass.Secondary, var: '--bs-secondary'},
        {name: ColorClass.Success, var: '--bs-success'},
        {name: ColorClass.Info, var: '--bs-info'},
        {name: ColorClass.Warning, var: '--bs-warning'},
        {name: ColorClass.Danger, var: '--bs-danger'},
        {name: ColorClass.Light, var: '--bs-light'},
        {name: ColorClass.Dark, var: '--bs-dark'}
    ];

    public static getBootstrapColorVar(name: string): string {
        const color = EnumUtils.BootstrapColors.find(x => x.name === name);
        return color ? `var(${color.var})` : name;
    }

    public static getEnumKeyByValue<E extends Record<string, string | number>>(enumObj: E, value: E[keyof E]): keyof E | undefined {
        return Object.keys(enumObj).find(key => enumObj[key as keyof E] === value) as keyof E | undefined;
    }

    public static getEnumOptions<E extends Record<string, string | number>>(
        enumObj: E,
        formatter?: (value: string) => string
    ): Array<{ value: E[keyof E], text: string }> {
        return Object.keys(enumObj)
            .filter(key => isNaN(Number(key)))
            .map(key => {
                const value = enumObj[key as keyof E];
                const text = formatter ? formatter(value as unknown as string) : capitalCase(value.toString());
                return {value, text};
            });
    }

    public static getMaterialStatusBadgeClass(status: MaterialStatus): string {
        switch (status) {
            case MaterialStatus.Active:
                return 'bg-success-subtle text-success';
            case MaterialStatus.Discontinued:
            case MaterialStatus.BeingDiscontinued:
                return 'bg-secondary-subtle text-secondary';
            case MaterialStatus.Forecast:
                return 'bg-warning-subtle text-warning';
            default:
                return 'bg-info-subtle text-info';
        }
    }

    public static getStorageUnitStatusBadgeClass(status: StorageUnitStatus): string {
        switch (status) {
            case StorageUnitStatus.Active:
                return 'bg-success-subtle text-success';
            case StorageUnitStatus.Completed:
                return 'bg-secondary-subtle text-secondary';
            default:
                return 'bg-info-subtle text-info';
        }
    }

    public static getIncidentStatusBadgeClass(status: IncidentStatus): string {
        switch (status) {
            case IncidentStatus.PendingApproval:
                return 'bg-info-subtle text-info';
            case IncidentStatus.Approved:
                return 'bg-success-subtle text-success';
            default:
                return 'bg-info-subtle text-info';
        }
    }

    public static getColorBadgeClass(color: ColorClass): string {
        switch (color) {
            case ColorClass.Primary:
                return 'bg-primary-subtle text-primary';
            case ColorClass.Secondary:
                return 'bg-secondary-subtle text-secondary';
            case ColorClass.Success:
                return 'bg-success-subtle text-success';
            case ColorClass.Info:
                return 'bg-info-subtle text-info';
            case ColorClass.Warning:
                return 'bg-warning-subtle text-warning';
            case ColorClass.Danger:
                return 'bg-danger-subtle text-danger';
            case ColorClass.Light:
                return 'bg-light-subtle text-light';
            case ColorClass.Dark:
                return 'bg-dark-subtle text-dark';
            default:
                return 'bg-secondary-subtle text-secondary';
        }
    }

    public static getAppointmentStatusBadgeClass(
        status: AppointmentStatus,
    ): string {
        switch (status) {
            case AppointmentStatus.Draft:
                return 'bg-secondary-subtle text-secondary';
            case AppointmentStatus.UnderReview:
                return 'bg-info-subtle text-info';
            case AppointmentStatus.Declined:
                return 'bg-danger-subtle text-danger';
            case AppointmentStatus.Approved:
                return 'bg-success-subtle text-success';
            case AppointmentStatus.CheckedIn:
                return 'bg-info-subtle text-info';
            case AppointmentStatus.ShuntMovePending:
                return 'bg-warning-subtle text-warning';
            case AppointmentStatus.NoShow:
                return 'bg-danger-subtle text-danger';
            case AppointmentStatus.ReadyToUnload:
                return 'bg-info-subtle text-info';
            case AppointmentStatus.Investigation:
                return 'bg-warning-subtle text-warning';
            case AppointmentStatus.Unloading:
                return 'bg-info-subtle text-info';
            case AppointmentStatus.TagsAsnPgiRequired:
                return 'bg-warning-subtle text-warning';
            case AppointmentStatus.Refused:
                return 'bg-danger-subtle text-danger';
            case AppointmentStatus.Completed:
                return 'bg-success-subtle text-success';
            case AppointmentStatus.OutboundRefused:
                return 'bg-danger-subtle text-danger';
            default:
                return 'bg-secondary-subtle text-secondary';
        }
    }

    public static getOrderAppointmentStatusBadgeClass(status: OrderAppointmentStatus | undefined): string {
        switch (status) {
            case OrderAppointmentStatus.NoAppointmentAssigned:
                return 'bg-secondary-subtle text-secondary';
            case OrderAppointmentStatus.AppointmentAssigned:
                return 'bg-info-subtle text-info';
            case OrderAppointmentStatus.AppointmentConfirmed:
                return 'bg-success-subtle text-success';
            case OrderAppointmentStatus.LtlLoad:
                return 'bg-warning-subtle text-warning';
            case OrderAppointmentStatus.ManualAppointmentSet:
                return 'bg-primary-subtle text-primary';
            default:
                return 'bg-secondary-subtle text-secondary';
        }
    }

    public static getShuntPriorityBadgeClass(
        status: ShuntPriority,
    ): string {
        switch (status) {
            case ShuntPriority.High:
                return 'bg-danger-subtle text-danger';
            case ShuntPriority.Medium:
                return 'bg-warning-subtle text-warning';
            case ShuntPriority.Low:
                return 'bg-success-subtle text-success';
            default:
                return 'bg-secondary-subtle text-secondary';
        }
    }

    public static getPurchaseStatusBadgeClass(status: PurchaseOrderStatus): string {
        switch (status) {
            case PurchaseOrderStatus.Open:
                return 'bg-secondary-subtle text-secondary';
            case PurchaseOrderStatus.Closed:
                return 'bg-success-subtle text-success';
            default:
                return 'bg-secondary-subtle text-secondary';
        }
    }

    public static getEmptyCheckInStatusBadgeClass(
        status: EmptyCheckInStatus,
    ): string {
        switch (status) {
            case EmptyCheckInStatus.Draft:
                return 'bg-secondary-subtle text-secondary';
            case EmptyCheckInStatus.EmptyRequired:
                return 'bg-success-subtle text-success';
            case EmptyCheckInStatus.EmptyNotRequired:
                return 'bg-danger-subtle text-danger';
            case EmptyCheckInStatus.Investigation:
                return 'bg-warning-subtle text-warning';
            case EmptyCheckInStatus.ShuntMovePending:
                return 'bg-warning-subtle text-warning';
            case EmptyCheckInStatus.OutboundEmpty:
                return 'bg-info-subtle text-info';
            case EmptyCheckInStatus.Loading:
                return 'bg-success-subtle text-success';
            case EmptyCheckInStatus.OutboundLoaded:
                return 'bg-info-subtle text-info';
            case EmptyCheckInStatus.Loaded:
                return 'bg-success-subtle text-success';
            default:
                return 'bg-secondary-subtle text-secondary';
        }
    }

    public static getEmptyCheckOutStatusBadgeClass(
        status: EmptyCheckOutStatus,
    ): string {
        switch (status) {
            case EmptyCheckOutStatus.ReadyForCheckout:
                return 'bg-secondary-subtle text-secondary';
            case EmptyCheckOutStatus.ReadyForCheckoutEmpty:
                return 'bg-secondary-subtle text-secondary';
            case EmptyCheckOutStatus.Investigation:
                return 'bg-warning-subtle text-warning';
            case EmptyCheckOutStatus.ShuntMovePending:
                return 'bg-warning-subtle text-warning';
            case EmptyCheckOutStatus.CheckedOut:
                return 'bg-success-subtle text-success';
            case EmptyCheckOutStatus.CheckedOutEmpty:
                return 'bg-success-subtle text-success';
            case EmptyCheckOutStatus.ReadyForCheckoutLoaded:
                return 'bg-secondary-subtle text-secondary';
            case EmptyCheckOutStatus.CheckedOutLoaded:
                return 'bg-success-subtle text-success';
            default:
                return 'bg-secondary-subtle text-secondary';
        }
    }

    public static getPositionStatusBadgeClass(status: PositionStatus | undefined): string {
        switch (status) {
            case PositionStatus.Active:
                return 'bg-success-subtle text-success';
            case PositionStatus.Leave:
                return 'bg-secondary-subtle text-secondary';
            case PositionStatus.Inactive:
                return 'bg-warning-subtle text-warning';
            case PositionStatus.Deactivated:
                return 'bg-danger-subtle text-danger';
            default:
                return 'bg-danger-subtle text-danger';
        }
    }

    public static getUserStatusBadgeClass(status: UserStatus | undefined): string {
        switch (status) {
            case UserStatus.Active:
                return 'bg-success-subtle text-success';
            case UserStatus.Leave:
                return 'bg-secondary-subtle text-secondary';
            case UserStatus.Inactive:
                return 'bg-warning-subtle text-warning';
            case UserStatus.Terminated:
                return 'bg-danger-subtle text-danger';
            default:
                return 'bg-secondary-subtle text-secondary';
        }
    }

    public static getInvoicePurchaseOrderStatusBadgeClass(status: InvoicePurchaseOrderStatus | undefined): string {
        switch (status) {
            case InvoicePurchaseOrderStatus.Draft:
                return 'bg-secondary-subtle text-secondary';
            case InvoicePurchaseOrderStatus.Open:
                return 'bg-info-subtle text-info';
            case InvoicePurchaseOrderStatus.Scheduled:
                return 'bg-info-subtle text-info';
            case InvoicePurchaseOrderStatus.Canceled:
                return 'bg-danger-subtle text-danger';
            case InvoicePurchaseOrderStatus.InvoiceMissed:
                return 'bg-danger-subtle text-danger';
            case InvoicePurchaseOrderStatus.InvoicedLate:
                return 'bg-info-subtle text-info';
            case InvoicePurchaseOrderStatus.InvoicePending:
                return 'bg-info-subtle text-info';
            case InvoicePurchaseOrderStatus.Posted:
                return 'bg-success-subtle text-success';
            case InvoicePurchaseOrderStatus.OnHold:
                return 'bg-warning-subtle text-warning';
            case InvoicePurchaseOrderStatus.Invoiced:
                return 'bg-warning-subtle text-warning';
            case InvoicePurchaseOrderStatus.Completed:
                return 'bg-success-subtle text-success';
            default:
                return 'bg-danger-subtle text-danger';
        }
    }

    public static getPreProductionStatusBadgeClass(status: PreProductionStatus | undefined): string {
        switch (status) {
            case PreProductionStatus.PoReceived:
                return 'bg-secondary-subtle text-secondary';
            case PreProductionStatus.Planning:
                return 'bg-info-subtle text-info';
            case PreProductionStatus.Rejection:
                return 'bg-danger-subtle text-danger';
            case PreProductionStatus.NoStock:
                return 'bg-warning-subtle text-warning';
            case PreProductionStatus.ReleasedToHdc:
                return 'bg-success-subtle text-success';
            case PreProductionStatus.LateReleasedToHdc:
                return 'bg-warning-subtle text-warning';
            case PreProductionStatus.ReleasedToHdcSto:
                return 'bg-success-subtle text-success';
            default:
                return 'bg-secondary-subtle text-secondary';
        }
    }

    public static getProductionStatusBadgeClass(status: ProductionStatus | undefined): string {
        switch (status) {
            case ProductionStatus.InProductionNoStaging:
                return 'bg-secondary-subtle text-secondary';
            case ProductionStatus.InProduction:
                return 'bg-info-subtle text-info';
            case ProductionStatus.PickingNoStaging:
                return 'bg-secondary-subtle text-secondary';
            case ProductionStatus.Picking:
                return 'bg-info-subtle text-info';
            case ProductionStatus.ReadyToLoadNoStaging:
                return 'bg-warning-subtle text-warning';
            case ProductionStatus.ReadyToLoad:
                return 'bg-success-subtle text-success';
            case ProductionStatus.ReadyForCheckout:
                return 'bg-secondary-subtle text-secondary';
            case ProductionStatus.CheckedOut:
                return 'bg-success-subtle text-success';
            case ProductionStatus.Loading:
                return 'bg-success-subtle text-success';
            case ProductionStatus.LoadedOnTime:
                return 'bg-success-subtle text-success';
            case ProductionStatus.LoadedLate:
                return 'bg-danger-subtle text-danger';
            default:
                return 'bg-secondary-subtle text-secondary';
        }
    }

    public static getInvoiceStatusBadgeClass(status: InvoiceStatus | undefined): string {
        switch (status) {
            case InvoiceStatus.Draft:
                return 'bg-secondary-subtle text-secondary';
            case InvoiceStatus.Submitted:
                return 'bg-info-subtle text-info';
            case InvoiceStatus.Posted:
                return 'bg-success-subtle text-success';
            case InvoiceStatus.OnHold:
                return 'bg-warning-subtle text-warning';
            case InvoiceStatus.L1Approval:
                return 'bg-info-subtle text-info';
            case InvoiceStatus.Declined:
                return 'bg-danger-subtle text-danger';
            case InvoiceStatus.Investigation:
                return 'bg-warning-subtle text-warning';
            case InvoiceStatus.OnHold:
                return 'bg-warning-subtle text-warning';
            case InvoiceStatus.L2Approval:
                return 'bg-info-subtle text-info';
            case InvoiceStatus.L3Approval:
                return 'bg-info-subtle text-info';
            case InvoiceStatus.PendingAP:
                return 'bg-info-subtle text-info';
            default:
                return 'bg-danger-subtle text-danger';
        }
    }

    public static getCreditRequestStatusBadgeClass(status: CreditRequestStatus | undefined): string {
        switch (status) {
            case CreditRequestStatus.Draft:
                return 'bg-secondary-subtle text-secondary';
            case CreditRequestStatus.PendingAP:
                return 'bg-info-subtle text-info';
            case CreditRequestStatus.Cancelled:
                return 'bg-danger-subtle text-danger';
            case CreditRequestStatus.Completed:
                return 'bg-success-subtle text-success';
            default:
                return 'bg-secondary-subtle text-secondary';
        }
    }

    public static getNestleStatusBadgeClass(status: NestleStatus | undefined): string {
        switch (status) {
            case NestleStatus.SapPendingNestle:
            case NestleStatus.DwaPendingNestle:
                return 'bg-warning-subtle text-warning';
            case NestleStatus.DwaPendingHopewell:
            case NestleStatus.NextViewPendingHopewell:
                return 'bg-primary-subtle text-primary';
            case NestleStatus.Active:
                return 'bg-success-subtle text-success';
            case NestleStatus.TerminationPending:
            case NestleStatus.Terminated:
                return 'bg-danger-subtle text-danger';
            case NestleStatus.DeactivationPending:
            case NestleStatus.Inactive:
            case NestleStatus.PasswordResetRequired:
                return 'bg-warning-subtle text-warning';
            default:
                return 'bg-secondary-subtle text-secondary';
        }
    }
}

export default EnumUtils
